import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetVehicleMappingByWheelId } from "../../../queries/useGetVehicleMappingByWheelId";
import { PackagePartItem } from "../../../queries/useSearchPartPackagesByVehicle";
import { usePackageEditor } from "../../../stores/hooks/usePackageEditor";
import { Dialog } from "../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../ui/Dialog/DialogHeader";
import { TabPanels, TabPanelsContext } from "../../../ui/TabPanels/TabPanels";
import { VehicleMapping } from "../VehicleMapping/VehicleMapping";
import { createVehicleMappingKey } from "../createVehicleMappingKey";
import { useVehicleMaping } from "../useVehicleMaping";
import { useVehicleMappingCriteria } from "../useVehicleMappingCriteria";
import { EditPackageTabs } from "./EditPackageTabs";
import { Footer } from "./Footer";
import { PackageInfo } from "./PackageInfo";
import { PackageParts } from "./PackageParts";

export const EditPackageDialog: FunctionComponent<{
  onClose: VoidFunction;
  selectedPackage?: PackagePartItem;
}> = ({ onClose, selectedPackage }) => {
  const { t } = useTranslation();
  const tabsContextRef = useRef<TabPanelsContext>();
  const [isSending, setIsSending] = useState(false);

  const [
    packageState,
    {
      getPartQuantity,
      setModelsAreLoading,
      setQuantity,
      setTabIndex,
      setVehicleManufacturers,
      setVehicleMapping,
      toggleVehicleManufacturerId,
      toggleVehicleYearId,
    },
  ] = usePackageEditor(selectedPackage?.partDetails.primaryPart.itemId || null);

  useEffect(() => {
    if (
      tabsContextRef.current &&
      packageState.tabIndex !== tabsContextRef.current.index
    ) {
      tabsContextRef.current.setIndex(packageState.tabIndex);
    }
  }, [packageState.tabIndex]);

  const [vehicleMappingCriteria, setVehicleMappingCriteria] =
    useVehicleMappingCriteria();

  const wheelPartId =
    selectedPackage?.partWheel?.partDetails.primaryPart.itemId ?? null;

  const { data: wheelMapping, isLoading } =
    useGetVehicleMappingByWheelId(wheelPartId);

  const fallbackVehicleMapping =
    (selectedPackage?.partSummary.primaryPart.vehicleIdList
      .map((vehicleId) => {
        const details = selectedPackage.partSummary.primaryPart.vehicleDetails;
        const vehicle = details[String(vehicleId)];
        if (!vehicle) {
          return null;
        }
        const vehicleKey = createVehicleMappingKey(vehicle);
        return vehicleKey;
      })
      .filter(Boolean) as string[] | undefined) || [];

  const data = useVehicleMaping({
    criteria: vehicleMappingCriteria,
    fallbackManufacturers: packageState.vehicleManufacturers.filter((item) => {
      const formattedTiresize =
        selectedPackage?.partTire?.partDetails.primaryPart.formattedTiresize;
      return formattedTiresize
        ? item.vehicleSubmodelTiresizeArray.some(
            (tireSizeItem) =>
              tireSizeItem.formattedTireSize === formattedTiresize
          )
        : true;
    }),
    partId: wheelPartId,
    setVehicleMapping,
    vehicleDetails: {
      ...selectedPackage?.partSummary.primaryPart.vehicleDetails,
      ...wheelMapping?.mappedVehicles,
    },
    vehicleMapping: packageState.vehicleMapping ?? fallbackVehicleMapping,
  });

  return (
    <Dialog
      className="md:w-[32rem]"
      id="edit-package-dialog"
      onClose={onClose}
      placement="top"
      visible={Boolean(selectedPackage)}
    >
      <DialogHeader onClose={onClose} title={t("packageEditor.editPackage")} />
      <EditPackageTabs
        selectTab={setTabIndex}
        tabIndex={packageState.tabIndex}
      />
      <TabPanels
        className="pb-4 pt-2"
        contextRef={tabsContextRef}
        defaultIndex={0}
        key={selectedPackage?.partDetails.primaryPart.itemId}
      >
        <PackageParts
          getPartQuantity={getPartQuantity}
          selectedPackage={selectedPackage}
          setQuantity={setQuantity}
        />
        <VehicleMapping
          criteria={vehicleMappingCriteria}
          data={data}
          isLoading={isLoading}
          setCriteria={setVehicleMappingCriteria}
          setModelsAreLoading={setModelsAreLoading}
          setVehicleManufacturers={setVehicleManufacturers}
          toggleVehicleManufacturerId={toggleVehicleManufacturerId}
          toggleVehicleYearId={toggleVehicleYearId}
          vehicleManufacturerIds={packageState.vehicleManufacturerIds}
          vehicleYearIds={packageState.vehicleYearIds}
        />
        <PackageInfo
          getPartQuantity={getPartQuantity}
          mappedIds={data.selectedVehicleIds}
          onUpdate={onClose}
          selectedPackage={selectedPackage}
          setIsSending={setIsSending}
        />
      </TabPanels>
      <Footer
        hide={onClose}
        isSending={isSending}
        modelsAreLoading={packageState.modelsAreLoading}
        selectTab={setTabIndex}
        shouldFetchVehicles={!data.vehicles.length}
        tabIndex={packageState.tabIndex}
        vehicleManufacturerIds={packageState.vehicleManufacturerIds}
        vehicleYearIds={packageState.vehicleYearIds}
      />
    </Dialog>
  );
};

/**
 * @see docs/worklog/TW-910-edit-package-vehicle-selector/rfc.md
 * @see docs/worklog/TW-910-edit-package-vehicle-selector/tfc.md
 */

/**
 * @see docs/worklog/TW-1010-edit-package-vehicle-mapping-reset/rfc.md
 * @see docs/worklog/TW-1010-edit-package-vehicle-mapping-reset/tfc.md
 */
